import React from 'react';
import { useRef } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import Chevron from 'components/Chevron/Chevron';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import store from 'store/Store';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import UrlUtils from 'utils/Url';
import {
    colors, space, fontSizes, lineHeights, fontWeights
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/locales/ResetPassword_fr_CA';

const POS_ACCOUNT_ERROR = 'ERR_400004';
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

/**
 * ResetPasswordLegacy Page (Auth/ResetPasswordLegacy)
 * Full-screen password reset request page for the legacy third-party OAuth2
 * `/resetpassword` route (recovered from the pre-SIM-1 Auth/ResetPassword page).
 * The modern SIM-18 flow lives at `/reset-password` (Auth/ResetPassword).
 *
 * Flow:
 * 1. User enters email address
 * 2. Submit email to backend reset password API endpoint
 * 3. On success: show success message modal
 * 4. User receives email with reset link
 */
const ResetPasswordLegacy = () => {
    const emailInput = useRef(null);

    const isValid = () => {
        const loginError = emailInput.current.validateError();

        return !loginError;
    };

    const dispatchShowInfoModal = errorMsg => {
        const email = emailInput.current.getValue();

        if (errorMsg) {
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('resetPassword'),
                    message: errorMsg,
                    buttonText: getText('confirmButton'),
                    bodyPaddingBottom: 4,
                    isHtml: true
                })
            );
        } else {
            store.dispatch(Actions.showResetPasswordConfirmationModal({ isOpen: true, email, onDismiss: handleClose }));
        }
    };

    const submit = e => {
        e.preventDefault();

        const email = emailInput.current.getValue();

        const successCallback = () => {
            dispatchShowInfoModal();
        };

        const failureCallback = async response => {
            const errorMsg = response.errorMessage || (response.errors || [])[0]?.errorMessage || getText('fallbackErrorMsg');
            const errorCode = (response.errors || [])[0]?.errorCode;

            if (errorCode === POS_ACCOUNT_ERROR) {
                store.dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email, isResetPasswordFlow: true }));
            } else {
                dispatchShowInfoModal(errorMsg);
            }
        };

        if (isValid()) {
            store.dispatch(UserActions.forgotPassword(email, successCallback, json => failureCallback(json), null));
        }
    };

    const handleClose = () => {
        const externalAuthParams = Storage.local.getItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS);
        let redirectUrl = '/login';

        if (externalAuthParams) {
            /* eslint-disable camelcase */
            const oauthParams = {
                response_type: externalAuthParams.responseType,
                client_id: externalAuthParams.clientId,
                redirect_uri: externalAuthParams.redirectUri,
                scope: externalAuthParams.scope,
                state: externalAuthParams.state,
                code_challenge: externalAuthParams.codeChallenge,
                code_challenge_method: externalAuthParams.codeChallengeMethod,
                nonce: externalAuthParams.nonce,
                prompt: externalAuthParams.prompt,
                display: externalAuthParams.display,
                max_age: externalAuthParams.maxAge,
                ui_locales: externalAuthParams.uiLocales,
                id_token_hint: externalAuthParams.idTokenHint,
                login_hint: externalAuthParams.loginHint,
                acr_values: externalAuthParams.acrValues
            };
            /* eslint-enable camelcase */

            // Filter out empty values
            const filteredParams = Object.fromEntries(Object.entries(oauthParams).filter(([, value]) => value));

            const queryString = new URLSearchParams(filteredParams).toString();

            if (queryString) {
                redirectUrl = `/login?${queryString}`;
            }
        }

        UrlUtils.redirectTo(redirectUrl);
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                css={styles.pageContainer}
            >
                {/* Back Button */}
                <Box
                    is='button'
                    type='button'
                    aria-label={getText('backToSignIn')}
                    css={styles.backButton}
                    onClick={handleClose}
                >
                    <Chevron
                        direction={'left'}
                        color={colors.black}
                        css={styles.chevron}
                    />
                </Box>

                {/* Logo */}
                <Box css={styles.logoContainer}>
                    <img
                        src='/img/ufe/sephora-logo.svg'
                        alt='Sephora'
                        css={styles.logo}
                    />
                </Box>

                {/* Page Title */}
                <Box
                    maxWidth='400px'
                    width='100%'
                    css={styles.titleContainer}
                >
                    <Text
                        is='h1'
                        fontSize={fontSizes.lg}
                        fontWeight={fontWeights.bold}
                        lineHeight={lineHeights.tight}
                    >
                        {getText('pageTitle')}
                    </Text>
                </Box>

                {/* Description */}
                <Box
                    maxWidth='400px'
                    width='100%'
                    css={styles.descriptionContainer}
                >
                    <Text
                        fontSize={fontSizes.md}
                        textAlign='center'
                        lineHeight={lineHeights.base}
                    >
                        {getText('pageDescription')}
                    </Text>
                </Box>

                {/* Form Container with 400px max-width */}
                <Box
                    maxWidth='400px'
                    width='100%'
                >
                    {/* Reset Password Form */}
                    <form
                        noValidate
                        onSubmit={submit}
                    >
                        <Box css={styles.inputContainer}>
                            <InputEmail
                                label={getText('emailPlaceholder')}
                                ref={c => {
                                    if (c !== null) {
                                        emailInput.current = c;
                                    }
                                }}
                                autoFocus={true}
                                hideAsterisk={true}
                            />
                        </Box>

                        {/* Send Reset Link Button */}
                        <Button
                            type='submit'
                            width='100%'
                            variant='primary'
                            paddingY={space[3]}
                        >
                            {getText('sendResetLinkButton')}
                        </Button>
                    </form>

                    {/* Help Section */}
                    <Box css={styles.helpSection}>
                        <Text
                            fontSize={fontSizes.xs}
                            color={colors.gray}
                            lineHeight={lineHeights.base}
                            textAlign='center'
                        >
                            <b>{getText('stillHavingTrouble')}</b>
                            <br />
                            {getText('unableToResetPassword')}{' '}
                            <Link
                                href='tel:1-877-737-4672'
                                color={colors.blue}
                                underline={true}
                            >
                                1-877-SEPHORA
                            </Link>{' '}
                            {getText('phoneNumber')}
                            {' | '}
                            {getText('forAssistance')}{' '}
                            <Link
                                color={colors.blue}
                                underline={true}
                                href='/beauty/accessibility'
                            >
                                {getText('accessibility')}
                            </Link>
                            .
                        </Text>
                    </Box>
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

const styles = {
    pageContainer: {
        paddingTop: space[7],
        paddingBottom: space[7],
        paddingLeft: space[3],
        paddingRight: space[3],
        backgroundColor: colors.white,
        position: 'relative'
    },
    backButton: {
        position: 'absolute',
        top: space[5],
        left: space[5],
        cursor: 'pointer',
        background: 'none',
        border: 0,
        padding: 0
    },
    chevron: {
        width: space[4],
        height: space[4]
    },
    logoContainer: {
        marginBottom: space[6]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    titleContainer: {
        marginBottom: space[4]
    },
    descriptionContainer: {
        marginBottom: space[6]
    },
    inputContainer: {
        marginBottom: space[5]
    },
    helpSection: {
        marginTop: space[6]
    }
};

export default wrapFunctionalComponent(ResetPasswordLegacy, 'ResetPasswordLegacy');
